import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, StatusBadge } from "../components/Shared";
import { ProgramForm } from "../components/programs/ProgramForm";
import { BatchList } from "../components/programs/BatchList";
import { ForceDeleteDialog } from "../components/programs/ForceDeleteDialog";
import { PROG } from "../constants/testIds";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function Programs() {
  const { user } = useAuth();
  const { t } = useLang();
  const canManage = user?.permissions?.includes("program.manage");
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const [programs, setPrograms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [forceDelete, setForceDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/programs");
      setPrograms(data.data);
      setSelected((s) => s || data.data[0]?.program_id || null);
    } catch (e) {
      toast.error(extractError(e).message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("programs.confirmDelete"))) return;
    try {
      await api.delete(`/admin/programs/${id}`);
      toast.success(t("common.deleted"));
      if (selected === id) setSelected(null);
      load();
    } catch (e) {
      const err = extractError(e);
      if (err.code === "PROGRAM_HAS_BATCHES" && isMasterAdmin) {
        setForceDelete(programs.find((p) => p.program_id === id) || { program_id: id });
        return;
      }
      toast.error(err.message);
    }
  };

  return (
    <div data-testid={PROG.page} className="space-y-8">
      <PageHeader eyebrow={t("programs.eyebrow")} title={t("programs.title")} subtitle={t("programs.subtitle")}>
        {canManage && (
          <Button data-testid={PROG.newProgramBtn} onClick={() => setEditing({})} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-11 px-5">
            <Plus className="h-4 w-4 mr-1" /> {t("programs.newProgram")}
          </Button>
        )}
      </PageHeader>

      {editing && <ProgramForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {forceDelete && (
        <ForceDeleteDialog kind="program" id={forceDelete.program_id} name={forceDelete.name}
          onClose={() => setForceDelete(null)}
          onDone={() => { if (selected === forceDelete.program_id) setSelected(null); load(); }} />
      )}

      {programs.length === 0 ? (
        <EmptyState testId={PROG.empty} text={t("programs.empty")} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-3">
            {programs.map((p) => (
              <Card
                key={p.program_id}
                data-testid={`program-card-${p.program_id}`}
                onClick={() => setSelected(p.program_id)}
                className={`cursor-pointer bg-[#161923] transition ${selected === p.program_id ? "border-[#FF3B30]/60" : "border-white/5 hover:border-white/15"}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-[Outfit] font-bold text-white text-lg">{p.name}</h3>
                      <p data-testid={`program-duration-${p.program_id}`} className="text-xs text-slate-500 mt-1">{p.duration_days} {t("programs.days")}</p>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  {p.description && <p className="text-sm text-slate-400 mt-3 line-clamp-2">{p.description}</p>}
                  {canManage && (
                    <div className="flex gap-2 mt-4">
                      <Button data-testid={`program-edit-${p.program_id}`} size="sm" variant="ghost" className="h-8 text-slate-300 hover:text-white hover:bg-white/5" onClick={(e) => { e.stopPropagation(); setEditing(p); }}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}
                      </Button>
                      <Button data-testid={`program-delete-${p.program_id}`} size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); remove(p.program_id); }}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("common.delete")}
                      </Button>
                      {isMasterAdmin && (
                        <Button data-testid={`program-force-delete-${p.program_id}`} size="sm" variant="ghost" className="h-8 text-[#FF8A80] hover:bg-[#FF3B30]/10" onClick={(e) => { e.stopPropagation(); setForceDelete(p); }}>
                          {t("del.manage")}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="lg:col-span-2">
            {selected && <BatchList programId={selected} program={programs.find((p) => p.program_id === selected)} />}
          </div>
        </div>
      )}
    </div>
  );
}
